import { useState } from 'react'
import { Avatar, AvatarFallback } from '@journ/ui/avatar'
import { Button } from '@journ/ui/button'
import { Checkbox } from '@journ/ui/checkbox'
import { cn } from '@journ/ui/cn'
import { Input } from '@journ/ui/input'
import { ScrollArea } from '@journ/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@journ/ui/tabs'
import { useIntlayer } from 'react-intlayer'
import type { ChatMember } from '../domain/chat'
import { useMemberLookup } from '../hooks/use-member-lookup'
import { topicInitials } from './topic-display'
import { Sheet } from '@/components/sheet'
import { Icons } from '@journ/ui/icons'
import { Container } from '@/components/container'

type NewConversationDialogProps = {
	onStartP2P: (peerId: string) => Promise<unknown>
	onCreateGroup: (name: string, memberIds: string[]) => Promise<unknown>
}

const MemberOption = ({
	member,
	selected,
	onToggle,
}: {
	member: ChatMember
	selected: boolean
	onToggle: () => void
}) => (
	<button
		type='button'
		onClick={onToggle}
		className={cn(
			'flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-accent',
			selected ? 'bg-accent' : undefined,
		)}
	>
		<Avatar className='h-8 w-8 rounded-md'>
			<AvatarFallback className='rounded-md text-xs'>{topicInitials(member.name)}</AvatarFallback>
		</Avatar>
		<span className='flex-1 truncate text-sm'>{member.name}</span>
		<Checkbox checked={selected} />
	</button>
)

// Start a 1:1 (pick one person) or create a group (name + multiple people).
const NewConversationDialog = ({ onStartP2P, onCreateGroup }: NewConversationDialogProps) => {
	const content = useIntlayer('chat')
	const [open, setOpen] = useState(false)
	const [query, setQuery] = useState('')
	const [groupName, setGroupName] = useState('')
	const [selected, setSelected] = useState<string[]>([])
	const members = useMemberLookup(open ? query : null)

	const reset = () => {
		setQuery('')
		setGroupName('')
		setSelected([])
	}

	const toggle = (id: string) => setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))

	const startDirect = async (peerId: string) => {
		const result = await onStartP2P(peerId)
		if (result) {
			setOpen(false)
			reset()
		}
	}

	const createGroup = async () => {
		if (!groupName.trim() || selected.length === 0) return
		const result = await onCreateGroup(groupName, selected)
		if (result) {
			setOpen(false)
			reset()
		}
	}

	return (
		<>
			<Button onClick={() => setOpen(true)} variant='ghost' size='icon' aria-label={content.newConversation.value}>
				<Icons.Add size={18} />
			</Button>
			<Sheet isOpen={open} toggle={() => setOpen(x => !x)}>
				<Container className='place-content-center h-full'>
					<Tabs defaultValue='dm'>
						<TabsList className='grid w-full grid-cols-2'>
							<TabsTrigger value='dm'>{content.directMessage}</TabsTrigger>
							<TabsTrigger value='group'>{content.newGroup}</TabsTrigger>
						</TabsList>

						<TabsContent value='dm' className='space-y-2'>
							<Input value={query} onChange={e => setQuery(e.target.value)} placeholder={content.searchPeople.value} />
							<ScrollArea className='h-64'>
								<div className='flex flex-col gap-0.5'>
									{members.map(member => (
										<button
											key={member.id}
											type='button'
											onClick={() => startDirect(member.id)}
											className='flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-accent'
										>
											<Avatar className='h-8 w-8 rounded-md'>
												<AvatarFallback className='rounded-md text-xs'>{topicInitials(member.name)}</AvatarFallback>
											</Avatar>
											<span className='flex-1 truncate text-sm'>{member.name}</span>
										</button>
									))}
								</div>
							</ScrollArea>
						</TabsContent>

						<TabsContent value='group' className='space-y-2'>
							<Input
								value={groupName}
								onChange={e => setGroupName(e.target.value)}
								placeholder={content.groupNamePlaceholder.value}
							/>
							<Input value={query} onChange={e => setQuery(e.target.value)} placeholder={content.searchPeople.value} />
							<ScrollArea className='h-48'>
								<div className='flex flex-col gap-0.5'>
									{members.map(member => (
										<MemberOption
											key={member.id}
											member={member}
											selected={selected.includes(member.id)}
											onToggle={() => toggle(member.id)}
										/>
									))}
								</div>
							</ScrollArea>
							<footer className='flex justify-end'>
								<Button onClick={createGroup} disabled={!groupName.trim() || selected.length === 0}>
									{content.create}
								</Button>
							</footer>
						</TabsContent>
					</Tabs>
				</Container>
			</Sheet>
		</>
	)
}

export { NewConversationDialog }
